/**
 * Merges a `getSessionCoveragePage` delta onto a previously-fetched
 * `SessionCoverage`, so a live poll can carry only the segments that changed
 * instead of the whole session. Replacement/deletion is keyed by
 * (camera_id, seg_num) — a segment in `delta.cameras` replaces the client's
 * copy, one listed in `delta.removed` is dropped. `delta.reload` means the
 * cursor can no longer be trusted; the caller must re-fetch with
 * `getSessionCoverage` instead of merging.
 */

import { SessionCoverage, SessionCoverageDelta, SessionCoverageSegment } from '../types';

export function mergeCoverageDelta(
  base: SessionCoverage,
  delta: SessionCoverageDelta,
): SessionCoverage | null {
  if (delta.reload) return null;

  const cameras: Record<string, SessionCoverageSegment[]> = {};
  for (const [cameraId, segments] of Object.entries(base.cameras)) {
    cameras[cameraId] = segments;
  }

  for (const [cameraId, segNums] of Object.entries(delta.removed ?? {})) {
    const drop = new Set(segNums);
    const existing = cameras[cameraId];
    if (existing) cameras[cameraId] = existing.filter((seg) => !drop.has(seg.seg_num));
  }

  for (const [cameraId, segments] of Object.entries(delta.cameras ?? {})) {
    const bySegNum = new Map((cameras[cameraId] ?? []).map((seg) => [seg.seg_num, seg]));
    for (const seg of segments) bySegNum.set(seg.seg_num, seg);
    cameras[cameraId] = Array.from(bySegNum.values()).sort((a, b) => a.seg_num - b.seg_num);
  }

  return {
    ...base,
    cameras,
    coverage_rev: delta.coverage_rev,
    coverage_life: delta.coverage_life,
    union: delta.union ?? base.union,
    audio_union: delta.audio_union ?? base.audio_union,
    video_union: delta.video_union ?? base.video_union,
    playable_union: delta.playable_union ?? base.playable_union,
    is_active: delta.is_active ?? base.is_active,
    snapshot_rev: delta.snapshot_rev !== undefined ? delta.snapshot_rev : base.snapshot_rev,
    snapshot_complete:
      delta.snapshot_complete !== undefined ? delta.snapshot_complete : base.snapshot_complete,
    audio_expected: delta.audio_expected !== undefined ? delta.audio_expected : base.audio_expected,
    rotation: delta.rotation ?? base.rotation,
  };
}
